import { useMemo, useCallback, useState, useEffect } from 'react';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { getTrialDaysRemaining } from '../../../domain/entities/Subscription';
import { usePermissions } from '../../../hooks/usePermissions';

// =============================================================================
// TIPOS
// =============================================================================

export type AlertSeverity = 'info' | 'warning' | 'critical' | 'blocked';
export type AlertType = 'trial_ending' | 'payment_pending' | 'trial_expired' | 'blocked' | 'no_plan' | null;

export interface SubscriptionAlertInfo {
    type: AlertType;
    severity: AlertSeverity;
    title: string;
    description: string;
    daysRemaining: number;
    endDate: Date | null;
    showModal: boolean;
    ctaLabel: string;
    ctaPath: 'subscription' | 'billing';
}

export interface UseSubscriptionAlertReturn {
    alert: SubscriptionAlertInfo | null;
    shouldShowBanner: boolean;
    shouldShowModal: boolean;
    dismissModal: () => void;
    formatCountdown: (days: number) => string;
    getProgressPercentage: () => number;
}

// =============================================================================
// CONSTANTES
// =============================================================================

const TRIAL_TOTAL_DAYS = 7;
const GRACE_PERIOD_DAYS = 5;
const CRITICAL_DAYS_THRESHOLD = 3;
const WARNING_DAYS_THRESHOLD = 3;

// =============================================================================
// HOOK
// =============================================================================

export const useSubscriptionAlert = (): UseSubscriptionAlertReturn => {
    const { currentOrganization, isLoading } = useOrganization();
    const { canViewBilling } = usePermissions();
    const [isModalDismissed, setIsModalDismissed] = useState(false);

    // Reset modal dismiss quando organização muda
    useEffect(() => {
        setIsModalDismissed(false);
    }, [currentOrganization?.id]);

    // Calcula informações de alerta
    const alert = useMemo((): SubscriptionAlertInfo | null => {
        if (!currentOrganization || isLoading) return null;

        const { subscriptionStatus, blockedAt, trialEndsAt, status, plan } = currentOrganization;
        const now = new Date();

        // 1. BLOQUEADO - Prioridade máxima
        if (subscriptionStatus === 'blocked') {
            return {
                type: 'blocked',
                severity: 'blocked',
                title: 'Conta Bloqueada',
                description: 'Sua conta foi bloqueada por inadimplência.',
                daysRemaining: 0,
                endDate: null,
                showModal: true,
                ctaLabel: 'Regularizar Pagamento',
                ctaPath: 'billing',
            };
        }

        // 2. SEM PLANO (usuário novo)
        const isValidPlan = plan === 'basic' || plan === 'pro' || plan === 'enterprise';
        if (!isValidPlan && subscriptionStatus !== 'trialing' && subscriptionStatus !== 'active') {
            return {
                type: 'no_plan',
                severity: 'info',
                title: 'Escolha seu Plano',
                description: 'Inicie seu teste grátis de 7 dias.',
                daysRemaining: TRIAL_TOTAL_DAYS,
                endDate: null,
                showModal: false,
                ctaLabel: 'Ver Planos',
                ctaPath: 'subscription',
            };
        }

        // 3. PAGAMENTO PENDENTE (past_due)
        if (subscriptionStatus === 'past_due' && blockedAt) {
            const blockDate = new Date(blockedAt);
            const daysRemaining = Math.max(0, Math.ceil((blockDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
            const severity: AlertSeverity = daysRemaining <= 2 ? 'critical' : 'warning';

            return {
                type: 'payment_pending',
                severity,
                title: 'Pagamento Pendente',
                description: daysRemaining > 0 
                    ? `Regularize em ${daysRemaining} dia${daysRemaining > 1 ? 's' : ''} para evitar bloqueio.`
                    : 'Conta será bloqueada hoje.',
                daysRemaining,
                endDate: blockDate,
                showModal: daysRemaining <= 2,
                ctaLabel: 'Atualizar Pagamento',
                ctaPath: 'billing',
            };
        }

        // 3.1 ASSINATURA CANCELADA com período de graça
        if (subscriptionStatus === 'canceled' && blockedAt) {
            const blockDate = new Date(blockedAt);
            const daysRemaining = Math.max(0, Math.ceil((blockDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
            
            if (daysRemaining > 0) {
                return {
                    type: 'payment_pending',
                    severity: 'warning',
                    title: 'Assinatura Cancelada',
                    description: `Acesso até ${blockDate.toLocaleDateString('pt-BR')}. Renove para manter.`,
                    daysRemaining,
                    endDate: blockDate,
                    showModal: daysRemaining <= 3,
                    ctaLabel: 'Renovar Assinatura',
                    ctaPath: 'subscription',
                };
            }
        }

        // 4. TRIAL EXPIRADO
        if (trialEndsAt) {
            const trialEndDate = new Date(trialEndsAt);
            if (trialEndDate < now && subscriptionStatus !== 'active') {
                return {
                    type: 'trial_expired',
                    severity: 'blocked',
                    title: 'Período de Teste Encerrado',
                    description: 'Escolha um plano para continuar.',
                    daysRemaining: 0,
                    endDate: trialEndDate,
                    showModal: true,
                    ctaLabel: 'Escolher Plano',
                    ctaPath: 'subscription',
                };
            }

            // 5. TRIAL ACABANDO
            if (subscriptionStatus === 'trialing' && trialEndDate > now) {
                const daysRemaining = getTrialDaysRemaining(trialEndDate);
                
                if (daysRemaining <= WARNING_DAYS_THRESHOLD) {
                    const severity: AlertSeverity = daysRemaining <= CRITICAL_DAYS_THRESHOLD ? 'critical' : 'warning';

                    return {
                        type: 'trial_ending',
                        severity,
                        title: daysRemaining === 0 ? 'Último Dia de Trial' : 'Trial Expirando',
                        description: daysRemaining === 0
                            ? 'Seu teste acaba hoje!'
                            : `Restam ${daysRemaining} dia${daysRemaining > 1 ? 's' : ''} de teste.`,
                        daysRemaining,
                        endDate: trialEndDate,
                        showModal: daysRemaining <= CRITICAL_DAYS_THRESHOLD,
                        ctaLabel: 'Escolher Plano',
                        ctaPath: 'subscription',
                    };
                }
            }
        }

        return null;
    }, [currentOrganization, isLoading]);

    const memberSafeAlert = useMemo((): SubscriptionAlertInfo | null => {
        if (!alert || canViewBilling) return alert;

        // Member: faturamento é responsabilidade do owner — não exibir CTAs de pagamento/plano
        if (
            alert.type === 'no_plan' ||
            alert.type === 'trial_ending' ||
            alert.type === 'trial_expired'
        ) {
            return null;
        }

        if (alert.type === 'payment_pending' || alert.type === 'blocked') {
            return {
                ...alert,
                description: 'A organização possui pendências de pagamento. Avise o administrador ou proprietário.',
                ctaLabel: '',
                ctaPath: 'profile',
                showModal: alert.severity === 'blocked',
            };
        }

        return null;
    }, [alert, canViewBilling]);

    // Formata countdown para exibição
    const formatCountdown = useCallback((days: number): string => {
        if (days === 0) return 'Hoje';
        if (days === 1) return '1 dia';
        return `${days} dias`;
    }, []);

    // Calcula porcentagem de progresso (para barra visual)
    const getProgressPercentage = useCallback((): number => {
        if (!memberSafeAlert) return 100;

        const { type, daysRemaining } = memberSafeAlert;
        
        if (type === 'trial_ending' || type === 'trial_expired') {
            return Math.max(0, (daysRemaining / TRIAL_TOTAL_DAYS) * 100);
        }
        
        if (type === 'payment_pending') {
            return Math.max(0, (daysRemaining / GRACE_PERIOD_DAYS) * 100);
        }

        return 0;
    }, [memberSafeAlert]);

    // Dismiss modal
    const dismissModal = useCallback(() => {
        setIsModalDismissed(true);
    }, []);

    // Determina se deve mostrar banner/modal
    const shouldShowBanner = Boolean(memberSafeAlert && memberSafeAlert.severity !== 'blocked');
    const shouldShowModal = Boolean(memberSafeAlert?.showModal && !isModalDismissed);

    return {
        alert: memberSafeAlert,
        shouldShowBanner,
        shouldShowModal,
        dismissModal,
        formatCountdown,
        getProgressPercentage,
    };
};
